import {
	ColumnOrderState,
	flexRender,
	Header,
	Row,
} from '@tanstack/react-table';
import { DataRowEventType, NvxDataGridProps } from '../../types';
import { Dispatch, MutableRefObject, SetStateAction, type FC } from 'react';
import { ExpandColumnCell } from '../ExpandColumn/ExpandColumnCell';
import { FileType } from 'Src/Types/File';
import { MOUSE_CLICK_TIMESTAMP } from '../../../../Constants/index';
import classNames from 'classnames';
import RowToolbarCell from '../RowToolbar';

interface TableRowProps extends Pick<
	NvxDataGridProps,
	| 'dataKey'
	| 'onGetRowClasses'
	| 'selectedRecords'
	| 'onClick'
	| 'setSelectedRecords'
	| 'isMultipleSelect'
	| 'onSelectionChanged'
	| 'rowExpanding'
	| 'checkBoxesMode'
	| 'rowToolbar'
> {
	row: Row<any>;
	dataKey: string;
	getIsExpanded: (recid: string) => boolean;
	selectionTimeStampRef: MutableRefObject<number>;
	onContextMenuHandler: (
		e: React.MouseEvent<Element, MouseEvent>,
		data: any,
	) => void;
	toggleExpanded: (recid: string) => void;
	columnOrder: ColumnOrderState;
	isActiveRowToolbarOn: string;
	setIsActiveRowToolbarOn: Dispatch<SetStateAction<string>>;
	onRowToolbarClickHandler: (
		e: React.MouseEvent<Element, MouseEvent>,
		data: any,
	) => void;
}

export const TableRow: FC<TableRowProps> = ({
	onClick,
	row,
	dataKey,
	getIsExpanded,
	onGetRowClasses,
	selectedRecords,
	selectionTimeStampRef,
	setSelectedRecords,
	isMultipleSelect,
	onSelectionChanged,
	onContextMenuHandler,
	rowExpanding,
	checkBoxesMode,
	toggleExpanded,
	rowToolbar,
	columnOrder,
	isActiveRowToolbarOn,
	setIsActiveRowToolbarOn,
	onRowToolbarClickHandler,
}) => {
	const recid = row.original[dataKey];
	const isExpand = getIsExpanded(recid);
	const externClass = onGetRowClasses ? onGetRowClasses(row, !!isExpand) : '';
	const rowClasses = classNames('rdc_datarow', externClass, {
		row_selected: !!selectedRecords && selectedRecords.includes(recid),
		row_expanded: isExpand,
	});

	/** Обработчик события MouseDown */
	const onMouseDown = (event: React.MouseEvent<Element, MouseEvent>) => {
		// Если нажата ЛКМ
		if (event.button === 0) {
			// запомним временную ветку
			selectionTimeStampRef.current = event.timeStamp;
			return;
		}
	};

	/** Обработчик выбора строки таблицы */
	const onRowSelect = (data: any) => {
		if (setSelectedRecords) {
			setTimeout(() => {
				setSelectedRecords((prev) => {
					let nextStateSelect = [...prev];
					const index = nextStateSelect.findIndex((i) => {
						return i === data[dataKey];
					});
					if (index !== -1) {
						nextStateSelect.splice(index, 1);
					} else if (isMultipleSelect) {
						nextStateSelect.push(data[dataKey]);
					} else {
						nextStateSelect = [data[dataKey]];
					}

					if (onSelectionChanged) {
						onSelectionChanged(nextStateSelect);
					}
					return nextStateSelect;
				});
			}, 50);
		}
	};

	/** Обработчик события MouseUp */
	const onMouseUp = (
		event: React.MouseEvent<Element, MouseEvent>,
		params: any,
	) => {
		// Если нажата ЛКМ
		if (event.button === 0) {
			// получаем разницу между нажатием и отпуском ЛКМ
			const diffTimeStamp =
				event.timeStamp - selectionTimeStampRef.current;
			// Если дольше константы
			if (diffTimeStamp > MOUSE_CLICK_TIMESTAMP) {
				// получаем выделенный текст
				const selected_text = window.getSelection()?.toString();
				// Если он есть, то выходим из функции, пущай юзер копирует...
				if (selected_text) {
					// тут в дальнейшим можно будет замутить автокопирование в буфер...
					return;
				}
			}
			// Костыльно конечно, но самый быстрый и быстродействующий способ
			// игнорить клик по кнопкам и инпутам
			const targetTagName = (
				event.target as Element
			).tagName.toLowerCase();
			const isInsideButton = !!(event.target as Element).closest(
				'button',
			);
			if (
				['input', 'svg', 'path', 'button'].includes(targetTagName) ||
				isInsideButton
			) {
				return;
			}
		}
		// далее обрабатываем клик обычно
		if (onClick) {
			let type: DataRowEventType = 'click';
			if (event.button == 1) {
				type = 'whClick';
			} else if (event.button === 2) {
				type = 'rightClick';
			}
			const result = onClick(
				event as React.MouseEvent<HTMLTableRowElement, MouseEvent>,
				type,
				params,
			);
			if (result !== undefined && result !== false) {
				onRowSelect(params);
			}
		}
	};

	/** Функция определяет последняя ли колонка */
	const isLastColumn = (id: string): boolean => {
		return (
			columnOrder.findIndex((c) => {
				return c === id;
			}) ===
			columnOrder.length - 1
		);
	};

	/** Возвращает ширину колонки  */
	const getColumnWidth = (
		header: Header<FileType, unknown>
	): number | '100%' => {
		const isLast = isLastColumn(header.id);
		if (isLast) {
			return '100%';
		}
		return header.getSize();
	};

	/**  Рендер ячейки таблицы */
	const renderCell = (cell: any) => {
		const isLast = isLastColumn(cell.column.id);
		return (
			<td
				key={cell.id}
				width={getColumnWidth(cell.column)}
				style={isLast ? { maxWidth: 'none' } : undefined}
				is-data-cell="true"
			>
				{flexRender(cell.column.columnDef.cell, cell.getContext())}
			</td>
		);
	};

	const renderRowToolbar = (data: any) => {
		if (!!rowToolbar && !!rowToolbar.onPrepareMenu) {
			return (
				<RowToolbarCell
					active={isActiveRowToolbarOn === data[dataKey]}
					data={data}
					options={rowToolbar}
					onClick={(e) => {
						setIsActiveRowToolbarOn(data[dataKey]);
						onRowToolbarClickHandler(e, data);
					}}
				/>
			);
		}
	};

	return (
		<tr
			key={row.id}
			id={recid}
			onMouseDown={onMouseDown}
			onMouseUp={(e) => {
				return onMouseUp(e, row.original);
			}}
			onContextMenu={(e) => {
				return onContextMenuHandler(e, row.original);
			}}
			className={rowClasses}
		>
			{(rowExpanding || checkBoxesMode) && (
				<ExpandColumnCell
					rowId={recid}
					isExpand={isExpand}
					rowExpanding={rowExpanding}
					onExpand={toggleExpanded}
					checkBoxesMode={checkBoxesMode}
					selectedRecords={selectedRecords}
					onRowSelect={() => {
						return onRowSelect(row.original);
					}}
				/>
			)}
			{row.getVisibleCells().map((cell: any) => {
				return renderCell(cell);
			})}
			{renderRowToolbar(row.original)}
		</tr>
	);
};
